import type { CSSProperties } from "react";

export type BrandColors = {
  backgroundColor: string;
  componentFillColor: string;
  componentFontColor: string;
  buttonColor: string;
  buttonCtaColor: string;
  footerColor: string;
};

export type FormTheme = ReturnType<typeof deriveTheme>;

type RGB = [number, number, number];

const INK = "#252525";
const WHITE = "#FFFFFF";
const BLACK = "#000000";
const RED = "#FF0000";

const SHADOW_SUBTLE = "0 1px 3px rgba(11,26,39,0.06)";
const SHADOW_REGULAR = "0 8px 16px rgba(11,26,39,0.08)";

const hexToRgb = (hex: string): RGB => {
  let h = String(hex || "#000")
    .replace("#", "")
    .trim();
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const n = parseInt(h.slice(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const rgbToHex = (rgb: number[]) =>
  "#" +
  rgb
    .map((v) =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase();

export const normalizeHex = (hex: string) => rgbToHex(hexToRgb(hex));

export const mix = (a: string, b: string, t: number) => {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex(A.map((v, i) => v + (B[i] - v) * t));
};

const luminance = (hex: string) => {
  const c = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

export const contrast = (a: string, b: string) => {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};

const isDark = (hex: string) => luminance(hex) < 0.18;

const bestInk = (bg: string) =>
  contrast(INK, bg) >= contrast(WHITE, bg) ? INK : WHITE;

// Mix fg toward black or white in 5% steps until it reaches `ratio` on bg; hue is kept.
const ensureContrast = (fg: string, bg: string, ratio: number) => {
  const start = normalizeHex(fg);
  if (contrast(start, bg) >= ratio) return start;
  const target = contrast(BLACK, bg) > contrast(WHITE, bg) ? BLACK : WHITE;
  for (let i = 1; i <= 20; i++) {
    const c = mix(start, target, i / 20);
    if (contrast(c, bg) >= ratio) return c;
  }
  return target;
};

export function deriveTheme(raw: BrandColors) {
  const page = normalizeHex(raw.backgroundColor);
  const card = normalizeHex(raw.componentFillColor);
  const btn = normalizeHex(raw.buttonColor);
  const cardDark = isDark(card);
  const pageDark = isDark(page);

  const text = ensureContrast(raw.componentFontColor, card, 4.5);
  const muted = ensureContrast(mix(text, card, 0.3), card, 4.5);
  const inputBg = cardDark ? mix(card, WHITE, 0.06) : WHITE;
  const inputText = ensureContrast(text, inputBg, 4.5);
  const errBorder = ensureContrast(RED, inputBg, 3);
  const consentBg = cardDark ? mix(card, WHITE, 0.035) : mix(card, text, 0.035);
  const btnCta = normalizeHex(raw.buttonCtaColor);
  const btnText = contrast(btnCta, btn) >= 3 ? btnCta : bestInk(btn);
  const cardEdge = contrast(card, page) < 1.2 || (pageDark && cardDark);

  return {
    scheme: cardDark ? "dark" : "light",
    page,
    card,
    text,
    muted,
    inputBg,
    inputText,
    placeholder: mix(inputText, inputBg, 0.4),
    border: mix(inputBg, inputText, 0.2),
    borderHover: mix(inputBg, inputText, 0.42),
    focus: inputText,
    errBorder,
    errText: ensureContrast(RED, card, 4.5),
    errTint: mix(inputBg, errBorder, 0.05),
    consentBg,
    btn,
    btnText,
    btnHover: isDark(btn) ? mix(btn, WHITE, 0.12) : mix(btn, BLACK, 0.08),
    btnPressed: isDark(btn) ? mix(btn, WHITE, 0.2) : mix(btn, BLACK, 0.16),
    btnEdge:
      contrast(btn, card) >= 3 ? "transparent" : ensureContrast(btn, card, 3),
    btnRing: `0 0 0 2px ${card}, 0 0 0 4px ${text}`,
    spinTrack: mix(btn, btnText, 0.35),
    highlight: mix(inputBg, btn, cardDark ? 0.18 : 0.12),
    check: ensureContrast(btn, inputBg, 3),
    link: ensureContrast(btn, consentBg, 4.5),
    footer: ensureContrast(raw.footerColor, page, 4.5),
    cardBorder: cardEdge
      ? cardDark
        ? mix(card, WHITE, 0.1)
        : mix(card, text, 0.1)
      : "transparent",
    cardShadow: pageDark ? "none" : `${SHADOW_SUBTLE}, ${SHADOW_REGULAR}`,
    menuShadow: cardDark
      ? "0 8px 24px rgba(0,0,0,0.5)"
      : "0 8px 16px rgba(11,26,39,0.12), 0 1px 3px rgba(11,26,39,0.08)",
  };
}

export const PASSENTRY_COLORS: BrandColors = {
  backgroundColor: "#F8F8F8",
  componentFillColor: "#FFFFFF",
  componentFontColor: "#252525",
  buttonColor: "#FF4935",
  buttonCtaColor: "#FFFFFF",
  footerColor: "#4C4C4C",
};

export const themeToCssVars = (t: FormTheme) => {
  const vars: Record<string, string> = {};
  Object.entries(t).forEach(([key, value]) => {
    vars[`--pe-${key.replace(/[A-Z]/g, (c) => "-" + c.toLowerCase())}`] = value;
  });
  return vars as CSSProperties;
};
